import { useCallback, type ReactElement } from 'react'
import type { OrpcClient } from '@/hooks/useClient'
import { useClient } from '@/hooks/useClient'
import { parseFilter, SelectRecord } from '../SelectRecord'
import type {
  RecordColumn,
  RecordFilterOperator,
  RecordSearchParams,
  RecordSearchResult,
} from '../SelectRecord'

// The UI patient shape consumed by the table. Flat, display-oriented — distinct from the FHIR/model
// Patient. `birthdate` is ISO ("YYYY-MM-DD"); the Birthdate column formats it to MM/DD/YYYY.
export type SelectPatientRow = {
  id: string
  firstName: string
  lastName: string
  status: string
  gender: string
  birthdate: string
  phone: string
  email: string
  state: string
  address: string
  city: string
  zip: string
}

type SelectPatientProps = {
  onSelect: (patient: SelectPatientRow) => void
  pageSize?: number
  columns?: RecordColumn<SelectPatientRow>[]
  className?: string
  // Forwarded to SelectRecord — fired on an Escape not consumed by clearing a focused filter field.
  onEscape?: () => void
  // Optional search hint (e.g. a name the assistant already had) used to pre-seed the global search.
  mask?: string
}

// ---- Model ↔ UI translation ----------------------------------------------------------------------

// Derived from the contract so the query shape stays in sync with the /v2/patients endpoint.
type ListPatientsInput = Parameters<OrpcClient['listPatients']>[0]
type PatientFilterExpr = NonNullable<ListPatientsInput['filter']>
type PatientOrderEntry = NonNullable<ListPatientsInput['order']>[number]

// PatientPortalStatus / PatientGender enum → display label (and back, for filtering).
const STATUS_LABELS = { invited: 'Invited', active: 'Active', inactive: 'Inactive' } as const
const GENDER_LABELS = {
  male: 'Male',
  female: 'Female',
  other: 'Other',
  unknown: 'Unknown',
} as const

// UI field → model string search field. status/gender (enums) and birthdate (date) are handled apart.
const STRING_FIELD: Partial<Record<keyof SelectPatientRow, string>> = {
  firstName: 'given',
  lastName: 'family',
  phone: 'phone',
  email: 'email',
  address: 'address',
  city: 'city',
  state: 'state',
  zip: 'zip',
}

// UI sort key → model order entry. `status` is intentionally absent: the model can't sort by the
// computed portal status, so sorting that column falls back to the default order.
const ORDER_ENTRY: Partial<
  Record<keyof SelectPatientRow, (dir: 'asc' | 'desc') => PatientOrderEntry>
> = {
  firstName: (dir) => ({ given: dir }),
  lastName: (dir) => ({ family: dir }),
  gender: (dir) => ({ gender: dir }),
  birthdate: (dir) => ({ birthday: dir }),
  phone: (dir) => ({ phone: dir }),
  email: (dir) => ({ email: dir }),
  address: (dir) => ({ address: dir }),
  city: (dir) => ({ city: dir }),
  state: (dir) => ({ state: dir }),
  zip: (dir) => ({ zip: dir }),
}

const leaf = (obj: Record<string, unknown>): PatientFilterExpr =>
  ({ type: 'filter', ...obj }) as PatientFilterExpr

// FilterString before/after are inclusive (≤/≥); the UI's gt/lt are strict — the boundary value is
// treated as a match here. Acceptable for a name/address picker.
function stringLeaf(field: string, op: RecordFilterOperator, operand: string): PatientFilterExpr {
  const cond =
    op === 'eq'
      ? { exact: operand }
      : op === 'neq'
        ? { not_exact: operand }
        : op === 'gt'
          ? { after: operand }
          : op === 'lt'
            ? { before: operand }
            : { contains: operand }
  return leaf({ [field]: cond })
}

// ISO date the model's FilterDate accepts; null when unparseable (the model ignores such filters too).
// `Date.parse` reads ISO date-only strings as UTC but other formats as local time, so reading the
// parts back with a single fixed timezone would shift the day for one of the two. An ISO `YYYY-MM-DD`
// operand is therefore returned as typed (only validated), and any other format is parsed and read
// back in LOCAL time — the frame it was parsed in — preserving the calendar day the user entered.
function toIsoDate(operand: string): string | null {
  const trimmed = operand.trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return Number.isNaN(Date.parse(trimmed)) ? null : trimmed
  }
  const t = Date.parse(trimmed)
  if (Number.isNaN(t)) return null
  const d = new Date(t)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

// The model has no date-substring operator, so `contains` on Birthdate is a no-op (skipped).
function dateLeaf(op: RecordFilterOperator, operand: string): PatientFilterExpr | null {
  if (op === 'contains') return null
  const iso = toIsoDate(operand)
  if (!iso) return null
  const cond =
    op === 'eq'
      ? { exact: iso }
      : op === 'neq'
        ? { not_exact: iso }
        : op === 'gt'
          ? { after: iso }
          : { before: iso }
  return leaf({ birthday: cond })
}

// Categorical (status/gender): match operand against the display labels and push the matching enum
// values as an `in` set. eq/neq are case-sensitive (matching the string columns); contains stays a
// case-insensitive substring. eq/contains → in [matches]; neq → not(in [matches]); gt/lt meaningless.
function enumExpr(
  field: 'status' | 'gender',
  labels: Record<string, string>,
  op: RecordFilterOperator,
  operand: string,
): PatientFilterExpr | null {
  if (op === 'gt' || op === 'lt') return null
  const keys = Object.keys(labels)
  const matched =
    op === 'contains'
      ? keys.filter((k) => labels[k].toLowerCase().includes(operand.toLowerCase()))
      : keys.filter((k) => labels[k] === operand)
  const inLeaf = leaf({ [field]: { in: matched } })
  if (op === 'neq') {
    return { type: 'expression', operator: 'not', operands: [inLeaf] } as PatientFilterExpr
  }
  return inLeaf
}

// Model string search fields backing the global search (STRING_FIELD's targets plus the full-name
// `name` field, which has no dedicated UI column).
const GLOBAL_STRING_FIELDS = [...Object.values(STRING_FIELD), 'name'] as string[]

// Global "search all columns": run the query through the SAME operator-prefix grammar as the
// per-column filters (parseFilter), then apply that operator to EVERY column at once as an OR — each
// column via the same translator it would use on its own. No prefix → contains across all columns;
// `=Male`, `>2000`, etc. behave exactly as they do in a single column, just unioned. enum/date
// columns that can't express the operator (or don't match) simply contribute nothing.
function globalExpr(raw: string): PatientFilterExpr | null {
  const parsed = parseFilter(raw)
  if (!parsed) return null
  const { operator, operand } = parsed
  const operands: PatientFilterExpr[] = GLOBAL_STRING_FIELDS.map((f) =>
    stringLeaf(f, operator, operand),
  )
  const gender = enumExpr('gender', GENDER_LABELS, operator, operand)
  if (gender) operands.push(gender)
  const status = enumExpr('status', STATUS_LABELS, operator, operand)
  if (status) operands.push(status)
  const birthday = dateLeaf(operator, operand)
  if (birthday) operands.push(birthday)
  return { type: 'expression', operator: 'or', operands } as PatientFilterExpr
}

function buildFilter(params: RecordSearchParams<SelectPatientRow>): PatientFilterExpr | undefined {
  const clauses: PatientFilterExpr[] = []
  for (const f of params.filters) {
    let expr: PatientFilterExpr | null = null
    if (f.field === 'status') expr = enumExpr('status', STATUS_LABELS, f.operator, f.operand)
    else if (f.field === 'gender') expr = enumExpr('gender', GENDER_LABELS, f.operator, f.operand)
    else if (f.field === 'birthdate') expr = dateLeaf(f.operator, f.operand)
    else {
      const modelField = STRING_FIELD[f.field]
      if (modelField) expr = stringLeaf(modelField, f.operator, f.operand)
    }
    if (expr) clauses.push(expr)
  }
  const g = globalExpr(params.globalSearch)
  if (g) clauses.push(g)
  if (clauses.length === 0) return undefined
  if (clauses.length === 1) return clauses[0]
  return { type: 'expression', operator: 'and', operands: clauses } as PatientFilterExpr
}

function labelOf(labels: Record<string, string>, value: string | null): string {
  return value ? (labels[value] ?? value) : ''
}

function formatDate(iso: string): string {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return y && m && d ? `${m}/${d}/${y}` : iso
}

// ---- Component -------------------------------------------------------------------------------------

export function StatusPill({ status }: { status: string }): ReactElement {
  const tone =
    status === 'Active'
      ? 'bg-[#dcfce7] text-[#166534]'
      : status === 'Invited'
        ? 'bg-[#dbeafe] text-[#1e40af]'
        : 'bg-[#f1f5f9] text-[#64748b]'
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${tone}`}>
      {status}
    </span>
  )
}

// Fixed widths (px) tuned to the patient dataset so columns don't shift as pages load; overflowing
// values truncate with an ellipsis.
const DEFAULT_COLUMNS: RecordColumn<SelectPatientRow>[] = [
  { key: 'firstName', header: 'First Name', width: 130 },
  { key: 'lastName', header: 'Last Name', width: 150 },
  // Portal status is computed server-side and has no `_sort` key, so it isn't sortable.
  {
    key: 'status',
    header: 'Status',
    width: 110,
    sortable: false,
    render: (r) => <StatusPill status={r.status} />,
  },
  { key: 'gender', header: 'Gender', width: 100 },
  { key: 'birthdate', header: 'Birthdate', width: 120, display: (r) => formatDate(r.birthdate) },
  { key: 'phone', header: 'Phone', width: 150 },
  {
    key: 'email',
    header: 'Email',
    width: 240,
    render: (r) => <span className="text-[#64748b]">{r.email}</span>,
  },
  { key: 'state', header: 'State', width: 80 },
  { key: 'address', header: 'Address', width: 190 },
  { key: 'city', header: 'City', width: 150 },
  { key: 'zip', header: 'Zip Code', width: 80 },
]

/**
 * A searchable, filterable, sortable, infinitely-scrolling patient picker backed by the /v2/patients
 * endpoint. Fills all provided horizontal and vertical space, so it can be dropped into a page region
 * or a modal dialog body. Selection is surfaced via `onSelect`.
 */
export function SelectPatient({
  onSelect,
  pageSize = 20,
  columns = DEFAULT_COLUMNS,
  className,
  onEscape,
  mask,
}: SelectPatientProps): ReactElement {
  const client = useClient()

  const search = useCallback(
    async (
      params: RecordSearchParams<SelectPatientRow>,
    ): Promise<RecordSearchResult<SelectPatientRow>> => {
      const orderBuilder = params.sortKey ? ORDER_ENTRY[params.sortKey] : undefined
      const order = orderBuilder ? [orderBuilder(params.sortDir === 1 ? 'asc' : 'desc')] : undefined

      const res = await client.listPatients({
        // limit is the absolute ceiling; MAX_SAFE_INTEGER = no cap, so each page fetches `count` rows.
        page: { offset: params.offset, count: params.limit, limit: Number.MAX_SAFE_INTEGER },
        filter: buildFilter(params),
        order,
      })

      const rows: SelectPatientRow[] = res.data.map((p) => ({
        id: p.id,
        firstName: p.given ?? '',
        lastName: p.family ?? '',
        status: labelOf(STATUS_LABELS, p.status),
        gender: labelOf(GENDER_LABELS, p.gender),
        birthdate: p.birthday ?? '',
        phone: p.phone ?? '',
        email: p.email ?? '',
        state: p.state ?? '',
        address: p.address ?? '',
        city: p.city ?? '',
        zip: p.zip ?? '',
      }))

      // `incompleteTotal` means the post-filter walk stopped early, so more matches may exist beyond
      // `total` — keep paging. `truncated` (Medplum's offset cap) means no more can be fetched.
      const hasMore =
        !res.page.truncated &&
        (Boolean(res.page.incompleteTotal) || params.offset + res.data.length < res.page.total)
      return { rows, total: res.page.total, hasMore, incompleteTotal: res.page.incompleteTotal }
    },
    [client],
  )

  return (
    <SelectRecord<SelectPatientRow>
      title="Select Patient"
      recordNoun="patient"
      slug="patients"
      columns={columns}
      getRowId={(p) => p.id}
      search={search}
      onSelect={onSelect}
      onEscape={onEscape}
      initialGlobalSearch={mask}
      pageSize={pageSize}
      className={className}
      renderSelectionSummary={(p) => (
        <>
          Selected:{' '}
          <strong className="font-semibold">
            {[p.firstName, p.lastName].filter(Boolean).join(' ')}
          </strong>{' '}
          · {p.gender || '—'} · DOB {formatDate(p.birthdate) || '—'} ·{' '}
          {[p.city, p.state].filter(Boolean).join(', ')}
        </>
      )}
    />
  )
}

export type { SelectPatientProps }
